<template>
    <b-container id="payment-deposit-accounts" v-if="$can('view-all')" fluid>
        <b-row class="page-title">
            <b-container fluid>
                <b-row>
                    <b-col cols="8">
                        <h3>Payment Deposit Accounts Management</h3>
                    </b-col>
                    <b-col cols="4" class="text-right">
                        <b-button variant="primary" @click="openCreateModal" class="btn-sm">
                            <i class="fas fa-plus"></i> New Deposit Account
                        </b-button>
                    </b-col>
                </b-row>
                <hr />
            </b-container>
        </b-row>

        <b-row>
            <b-col cols="12">
                <b-table 
                    :items="depositAccounts" 
                    :fields="fields" 
                    :busy="isBusy"
                    hover 
                    responsive 
                    striped
                    :per-page="perPage"
                    :current-page="currentPage"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    @filtered="onFiltered"
                >
                    <template v-slot:table-busy>
                        <div class="text-center text-danger my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                    </template>

                    <template v-slot:cell(name)="row">
                        <strong>{{ row.item.name }}</strong>
                        <b-badge v-if="row.item.is_default" variant="primary" class="ml-1">Default</b-badge>
                    </template>

                    <template v-slot:cell(account_number)="row">
                        <span v-if="row.item.account_number">{{ row.item.account_number }}</span>
                        <span v-else class="text-muted">No account number</span>
                    </template>

                    <template v-slot:cell(active)="row">
                        <b-badge :variant="row.item.active ? 'success' : 'secondary'">
                            {{ row.item.active ? 'Active' : 'Inactive' }}
                        </b-badge>
                    </template>

                    <template v-slot:cell(usage_count)="row">
                        <b-badge 
                            :variant="row.item.usage_count > 0 ? 'info' : 'light'"
                            :class="{ 'clickable-badge': row.item.usage_count > 0 }"
                            @click="row.item.usage_count > 0 ? viewPayments(row.item) : null"
                            style="cursor: pointer;"
                        >
                            {{ row.item.usage_count }} payment(s)
                        </b-badge>
                    </template>

                    <template v-slot:cell(actions)="row">
                        <b-button-group size="sm">
                            <b-button variant="outline-primary" @click="editDepositAccount(row.item)">
                                <i class="fas fa-edit"></i>
                            </b-button>
                            <b-button
                                variant="outline-danger"
                                @click="deleteDepositAccount(row.item)"
                                :disabled="row.item.is_default || row.item.usage_count > 0"
                            >
                                <i class="fas fa-trash"></i>
                            </b-button>
                        </b-button-group>
                    </template>
                </b-table>

                <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    aria-controls="payment-deposit-accounts-table"
                    class="mt-3"
                ></b-pagination>
            </b-col>
        </b-row>

        <!-- Create/Edit Modal -->
        <b-modal 
            :id="modalId" 
            :title="modalTitle" 
            @hidden="resetModal"
            @ok="saveDepositAccount"
            :ok-disabled="!isFormValid"
        >
            <b-form @submit.prevent="saveDepositAccount">
                <b-form-group label="Name" label-for="name" :invalid-feedback="errors.name">
                    <b-form-input 
                        id="name" 
                        v-model="form.name" 
                        :state="errors.name ? false : null"
                        required
                    ></b-form-input>
                </b-form-group>

                <b-form-group label="Account Number" label-for="account_number" :invalid-feedback="errors.account_number">
                    <b-form-input 
                        id="account_number" 
                        v-model="form.account_number" 
                        :state="errors.account_number ? false : null"
                    ></b-form-input>
                </b-form-group>

                <b-form-group>
                    <b-form-checkbox v-model="form.active" switch>
                        Active
                    </b-form-checkbox>
                </b-form-group>

                <b-form-group>
                    <b-form-checkbox v-model="form.is_default" switch>
                        Default for credit card payments
                    </b-form-checkbox>
                    <small class="text-muted">Used automatically for Kaja / credit card charges.</small>
                </b-form-group>
            </b-form>

            <template v-slot:modal-footer>
                <b-button variant="secondary" @click="closeModal">Cancel</b-button>
                <b-button variant="primary" @click="saveDepositAccount" :disabled="!isFormValid">
                    {{ isEdit ? 'Update' : 'Create' }}
                </b-button>
            </template>
        </b-modal>

        <!-- Payments Modal -->
        <b-modal 
            id="payments-modal" 
            title="Payments" 
            size="xl"
            @hidden="resetPaymentsModal"
        >
            <div v-if="selectedDepositAccount">
                <h6>Payments for: <strong>{{ selectedDepositAccount.name }}</strong></h6>
                <hr>
            </div>

            <b-table 
                :items="payments" 
                :fields="paymentFields" 
                :busy="paymentsBusy"
                hover 
                responsive 
                striped
                :per-page="paymentsPerPage"
                :current-page="paymentsCurrentPage"
            >
                <template v-slot:table-busy>
                    <div class="text-center text-danger my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong>Loading...</strong>
                    </div>
                </template>

                <template v-slot:cell(customer)="row">
                    <span v-if="row.item.customer">{{ row.item.customer.name }}</span>
                    <span v-else class="text-muted">No Customer</span>
                </template>

                <template v-slot:cell(amount_total)="row">
                    ${{ parseFloat(row.item.amount_total).toFixed(2) }}
                </template>

                <template v-slot:cell(amount_available)="row">
                    ${{ parseFloat(row.item.amount_available).toFixed(2) }}
                </template>

                <template v-slot:cell(status)="row">
                    <b-badge :variant="getStatusVariant(row.item.status)">
                        {{ row.item.status || 'N/A' }}
                    </b-badge>
                </template>

                <template v-slot:cell(date)="row">
                    {{ formatDate(row.item.date) }}
                </template>
            </b-table>

            <b-pagination
                v-model="paymentsCurrentPage"
                :total-rows="paymentsTotalRows"
                :per-page="paymentsPerPage"
                aria-controls="payments-table"
                class="mt-3"
            ></b-pagination>

            <template v-slot:modal-footer>
                <b-button variant="secondary" @click="closePaymentsModal">Close</b-button>
            </template>
        </b-modal>
    </b-container>
</template>

<script>
export default {
    name: 'PaymentDepositAccounts',
    
    data() {
        return {
            depositAccounts: [],
            isBusy: false,
            perPage: 10,
            currentPage: 1,
            totalRows: 0,
            sortBy: 'name',
            sortDesc: false,
            
            fields: [
                { key: 'name', label: 'Name', sortable: true },
                { key: 'account_number', label: 'Account Number', sortable: true },
                { key: 'active', label: 'Status', sortable: true },
                { key: 'usage_count', label: 'Usage', sortable: true },
                { key: 'actions', label: 'Actions', sortable: false }
            ],
            
            // Modal states
            modalId: 'deposit-account-modal',
            isEdit: false,
            
            // Form
            form: {
                id: null,
                name: '',
                account_number: '',
                active: true,
                is_default: false
            },
            
            errors: {},
            
            // Payments modal data
            payments: [],
            selectedDepositAccount: null,
            paymentsBusy: false,
            paymentsPerPage: 10,
            paymentsCurrentPage: 1,
            paymentsTotalRows: 0,
            
            paymentFields: [
                { key: 'customer', label: 'Customer', sortable: true },
                { key: 'amount_total', label: 'Total Amount', sortable: true },
                { key: 'amount_available', label: 'Available', sortable: true },
                { key: 'status', label: 'Status', sortable: true },
                { key: 'reference', label: 'Reference', sortable: true },
                { key: 'date', label: 'Date', sortable: true }
            ]
        }
    },
    
    computed: {
        modalTitle() {
            return this.isEdit ? 'Edit Payment Deposit Account' : 'Create Payment Deposit Account'
        },
        
        isFormValid() {
            return this.form.name.trim() !== ''
        }
    },
    
    methods: {
        async loadDepositAccounts() {
            this.isBusy = true
            try {
                const response = await axios.get('/api/company/payment-deposit-accounts')
                if (response.data.status === 'success') {
                    this.depositAccounts = response.data.deposit_accounts
                    this.totalRows = this.depositAccounts.length
                }
            } catch (error) {
                this.showAlert('error', 'Error loading payment deposit accounts', error.response?.data?.message || 'Unknown error')
            } finally {
                this.isBusy = false
            }
        },
        
        openCreateModal() {
            this.isEdit = false
            this.resetForm()
            this.$bvModal.show(this.modalId)
        },
        
        editDepositAccount(depositAccount) {
            this.isEdit = true
            this.form = { 
                ...depositAccount,
                active: Boolean(depositAccount.active),
                is_default: Boolean(depositAccount.is_default)
            }
            this.$bvModal.show(this.modalId)
        },
        
        async saveDepositAccount() {
            this.errors = {}
            
            try {
                const url = this.isEdit ? `/api/company/payment-deposit-accounts/${this.form.id}` : '/api/company/payment-deposit-accounts'
                const method = this.isEdit ? 'put' : 'post'
                
                const response = await axios[method](url, this.form)
                
                if (response.data.status === 'success') {
                    this.showAlert('success', 'Success', response.data.message)
                    this.closeModal()
                    this.loadDepositAccounts()
                }
            } catch (error) {
                if (error.response?.data?.errors) {
                    this.errors = error.response.data.errors
                } else {
                    this.showAlert('error', 'Error', error.response?.data?.message || 'Unknown error')
                }
            }
        },
        
        async deleteDepositAccount(depositAccount) {
            if (depositAccount.is_default) {
                this.showAlert('error', 'Cannot Delete', 'Set another account as default before deleting this one')
                return
            }

            if (depositAccount.usage_count > 0) {
                this.showAlert('error', 'Cannot Delete', 'This payment deposit account is being used by payments')
                return
            }
            
            if (confirm(`Are you sure you want to delete "${depositAccount.name}"?`)) {
                try {
                    const response = await axios.delete(`/api/company/payment-deposit-accounts/${depositAccount.id}`)
                    if (response.data.status === 'success') {
                        this.showAlert('success', 'Success', response.data.message)
                        this.loadDepositAccounts()
                    }
                } catch (error) {
                    this.showAlert('error', 'Error', error.response?.data?.message || 'Unknown error')
                }
            }
        },
        
        resetForm() {
            this.form = {
                id: null,
                name: '',
                account_number: '',
                active: true,
                is_default: false
            }
            this.errors = {}
        },
        
        closeModal() {
            this.$bvModal.hide(this.modalId)
            this.resetForm()
        },
        
        resetModal() {
            this.resetForm()
        },
        
        onFiltered(filteredItems) {
            this.totalRows = filteredItems.length
            this.currentPage = 1
        },
        
        showAlert(type, title, message) {
            this.$bvToast.toast(message, {
                title: title,
                variant: type,
                solid: true,
                autoHideDelay: 5000
            })
        },
        
        async viewPayments(depositAccount) {
            this.selectedDepositAccount = depositAccount
            this.paymentsBusy = true
            this.paymentsCurrentPage = 1
            
            try {
                const response = await axios.get(`/api/company/payment-deposit-accounts/${depositAccount.id}/payments`)
                if (response.data.status === 'success') {
                    this.payments = response.data.payments
                    this.paymentsTotalRows = this.payments.length
                    this.$bvModal.show('payments-modal')
                }
            } catch (error) {
                this.showAlert('error', 'Error loading payments', error.response?.data?.message || 'Unknown error')
            } finally {
                this.paymentsBusy = false
            }
        },
        
        getStatusVariant(status) {
            const statusMap = {
                'pending': 'warning',
                'completed': 'success',
                'failed': 'danger',
                'cancelled': 'secondary'
            }
            return statusMap[status?.toLowerCase()] || 'secondary'
        },
        
        formatDate(dateString) {
            if (!dateString) return 'N/A'
            const date = new Date(dateString)
            return date.toLocaleDateString()
        },
        
        closePaymentsModal() {
            this.$bvModal.hide('payments-modal')
        },
        
        resetPaymentsModal() {
            this.payments = []
            this.selectedDepositAccount = null
            this.paymentsCurrentPage = 1
            this.paymentsTotalRows = 0
        }
    },
    
    mounted() {
        this.loadDepositAccounts()
    }
}
</script>

<style scoped>
#payment-deposit-accounts {
    margin-top: 80px;
}

.page-title {
    margin-bottom: 20px;
}

.table th {
    border-top: none;
}

.badge {
    font-size: 0.75em;
}

.btn-group .btn {
    margin-right: 2px;
}

.btn-group .btn:last-child {
    margin-right: 0;
}

.clickable-badge {
    transition: all 0.2s ease;
}

.clickable-badge:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}
</style>
